import React, { useEffect, useRef, useState } from "react";

import clsx from "clsx";
import { motion } from "framer-motion";
import { debounce } from "lodash";

import { useUser } from "@components/user-context";
import { usePrettyLogEventPage } from "@components/v2/analytics";
import {
  type ExportMethodConfig,
  type ScheduleConfig,
  type ScheduledTimeFrame,
} from "@features/data-export/types";
import { VALIDATE_SCHEDULE_NAME } from "@gql/data-export";
import { useFeatureFlags, useNorthbeamMutation } from "@hooks/http";
import {
  type ValidateScheduleName,
  type ValidateScheduleNameVariables,
} from "@north-beam/nb-client-side";
import {
  DataExportFrequency,
  DataExportMethod,
} from "@north-beam/nb-client-side";
import { Radio } from "@shared/radio-group";
import Spinner from "@shared/spinner";

import { DailyConfigurator } from "./daily-configurator";
import { MonthlyConfigurator } from "./monthly-configurator";
import { RecipientsSelector } from "./recipients-selector";
import { WeeklyConfigurator } from "./weekly-configurator";

interface Props {
  scheduleConfig: ScheduleConfig;
  exportMethodConfig: ExportMethodConfig;
  isLoadingEmailRecipients: boolean;
  initialRecipients: string[];
  loadEmailRecipients(): void;
  onChangeSchedule(config: ScheduleConfig): void;
  onClose(): void;
}

export function ScheduleConfigurator({
  scheduleConfig,
  exportMethodConfig,
  initialRecipients,
  isLoadingEmailRecipients,
  loadEmailRecipients,
  onChangeSchedule,
  onClose,
}: Props) {
  const prettyLogEventPage = usePrettyLogEventPage();
  const { user } = useUser();
  const { hasFeatureFlag } = useFeatureFlags();

  const [validateScheduleName, { loading }] = useNorthbeamMutation<
    ValidateScheduleName,
    ValidateScheduleNameVariables
  >(VALIDATE_SCHEDULE_NAME);

  const [scheduleName, setScheduleName] = useState(scheduleConfig.name);
  const [validationError, setValidationError] = useState(false);

  useEffect(() => {
    if (exportMethodConfig.method !== DataExportMethod.EXTERNAL_GCS_BUCKET) {
      loadEmailRecipients();
    }
  }, [loadEmailRecipients, exportMethodConfig]);

  const addTimeFrame = (timeFrame: ScheduledTimeFrame) => {
    const timeFrames = [...scheduleConfig.timeFrames, timeFrame];

    prettyLogEventPage({
      domain: "DataExport",
      action: "onClick",
      additionalActionInfo: "Data export time frame added",
      data: {
        newTimeFrames: timeFrame,
        pageContext: { timeFrames: scheduleConfig.timeFrames },
      },
    });

    onChangeSchedule({
      ...scheduleConfig,
      timeFrames,
    });
  };

  const removeTimeFrame = (timeFrame: ScheduledTimeFrame) => {
    const timeFrames = scheduleConfig.timeFrames.filter((e) => e !== timeFrame);

    prettyLogEventPage({
      domain: "DataExport",
      action: "onClick",
      additionalActionInfo: "Data export time frame removed",
      data: {
        newTimeFrames: timeFrame,
        pageContext: { timeFrames: scheduleConfig.timeFrames },
      },
    });

    onChangeSchedule({
      ...scheduleConfig,
      timeFrames,
    });
  };

  const changeTimeFrames = (timeFrames: ScheduledTimeFrame[]) => {
    prettyLogEventPage({
      domain: "DataExport",
      action: "onClick",
      additionalActionInfo: "Data export time frame changed",
      data: {
        newTimeFrames: timeFrames,
        pageContext: { timeFrames: scheduleConfig.timeFrames },
      },
    });

    onChangeSchedule({ ...scheduleConfig, timeFrames });
  };

  const debouncedValidateScheduleName = useRef(
    debounce(async (config: ScheduleConfig, value: string) => {
      const { data: validateScheduleNameData } = await validateScheduleName({
        variables: { scheduleName: value },
      });

      if (validateScheduleNameData?.validateScheduleName.status === "ok") {
        onChangeSchedule({ ...config, name: value });
        setScheduleName(value);
      } else {
        setValidationError(true);
      }
    }, 1000),
  );

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.1 }}
      className="absolute h-full w-full right-0 z-50 bg-cosmic-4/90
                 grid grid-cols-[auto_minmax(0,34rem)_22rem] overflow-hidden"
    >
      <motion.div
        animate={{ translateX: 0 }}
        transition={{ duration: 0.2, ease: "easeOut" }}
        style={{ translateX: 200 }}
        className="col-start-2 bg-white border-l border-gray-100
                   grid grid-rows-[fit-content(100%)_auto] overflow-hidden"
      >
        <div className="py-5 px-8">
          <button
            className="text-blue-500 text-xs font-normal"
            onClick={() => onClose()}
          >
            <i
              className={clsx(
                "fa-regular fa-sharp fa-arrow-left text-[0.6rem]",
                "cursort-pointer mr-1.5",
              )}
            />
            Back
          </button>
        </div>
        <div className="px-8 overflow-auto">
          <div className="px-0 py-2 xl:px-10">
            <h3 className="text-base font-semibold m-0 mb-2">
              Select report frequency
            </h3>
            <p className="text-xs text-zinc-500 font-light m-0 mb-7">
              This is the number of days your report will be exported and
              automatically sent
            </p>
            <div className="text-[12px] font-light space-y-3 xl:space-y-4">
              <div>
                <Radio
                  label="Daily"
                  value="daily"
                  checked={
                    scheduleConfig.frequency === DataExportFrequency.DAILY
                  }
                  onChange={(val) => {
                    prettyLogEventPage({
                      domain: "DataExport",
                      action: "onSelect",
                      data: {
                        frequency: "daily",
                      },
                      additionalActionInfo: "Data export schedule frequency",
                    });

                    onChangeSchedule({
                      ...scheduleConfig,
                      frequency: DataExportFrequency.DAILY,
                      timeFrames: [{ day: "*", time: "08:00" }],
                    });
                  }}
                  labelClassName="cursor-pointer"
                  inputClassName="cursor-pointer h-3 w-3"
                />
              </div>
              <div>
                <Radio
                  label="Weekly"
                  value="weekly"
                  checked={
                    scheduleConfig.frequency === DataExportFrequency.WEEKLY
                  }
                  onChange={(val) => {
                    prettyLogEventPage({
                      domain: "DataExport",
                      action: "onSelect",
                      data: {
                        frequency: "weekly",
                      },
                      additionalActionInfo: "Data export schedule frequency",
                    });

                    onChangeSchedule({
                      ...scheduleConfig,
                      frequency: DataExportFrequency.WEEKLY,
                      timeFrames: [{ day: "1", time: "08:00" }],
                    });
                  }}
                  labelClassName="cursor-pointer"
                  inputClassName="cursor-pointer h-3 w-3"
                />
              </div>
              <div>
                <Radio
                  label="Monthly"
                  value="monthly"
                  checked={
                    scheduleConfig.frequency === DataExportFrequency.MONTHLY
                  }
                  onChange={(val) => {
                    prettyLogEventPage({
                      domain: "DataExport",
                      action: "onSelect",
                      data: {
                        frequency: "monthly",
                      },
                      additionalActionInfo: "Data export schedule frequency",
                    });

                    onChangeSchedule({
                      ...scheduleConfig,
                      frequency: DataExportFrequency.MONTHLY,
                      timeFrames: [{ day: "1", time: "08:00" }],
                    });
                  }}
                  labelClassName="cursor-pointer"
                  inputClassName="cursor-pointer h-3 w-3"
                />
              </div>
            </div>
          </div>
          {exportMethodConfig.method !==
            DataExportMethod.EXTERNAL_GCS_BUCKET && (
            <div className="px-0 py-5 xl:px-10 xl:py-7 xl:pb-0 pb-3">
              <h3 className="text-base font-semibold m-0 mb-2">
                Select email recipient(s)
              </h3>
              <p className="text-xs text-zinc-500 font-light m-0 mb-6">
                Your exports will be sent to the email addresses selected below
                with the timeframe(s) selected above
              </p>
              <RecipientsSelector
                allowCreatingOptions={hasFeatureFlag(
                  "enableDataExportToExternalUsers",
                )}
                isLoading={isLoadingEmailRecipients}
                initialOptions={initialRecipients.map((recipientEmail) => ({
                  label: recipientEmail,
                  value: recipientEmail,
                }))}
                tags={scheduleConfig.recipients.map((recipientEmail) => ({
                  label: recipientEmail,
                  value: recipientEmail,
                }))}
                onChange={(selectedRecipients) => {
                  onChangeSchedule({
                    ...scheduleConfig,
                    recipients: selectedRecipients.map((e) => e.value),
                  });
                }}
              />
            </div>
          )}
          {user.featureFlags.enableDataExportManagementPage && (
            <div className="px-0 py-5 xl:px-10 xl:py-7">
              <h3 className="text-base font-semibold m-0 mb-2">
                Enter schedule name
              </h3>
              <p className="text-xs text-zinc-500 font-light m-0 mb-6">
                Your exports will be accessible via the &quot;Manage
                exports&quot; section of your settings page
              </p>
              <div className="w-[70%] flex items-center">
                <input
                  className="text-xs font-light shadow-none border border-[#D1D5DB]
                            focus:border-[#3B82F6] rounded-md px-3 py-2 outline-0
                            flex-1"
                  type="text"
                  value={scheduleName}
                  onChange={(ev) => {
                    const val = ev.target.value;
                    setValidationError(false);
                    onChangeSchedule({ ...scheduleConfig, name: "" });
                    setScheduleName(val);
                    debouncedValidateScheduleName.current(scheduleConfig, val);
                  }}
                />
                <div className="text-[#ccc] text-xs ml-2 w-[20px]">
                  {loading && <Spinner />}
                </div>
              </div>
              {validationError && (
                <div className="text-xs font-light mt-1 text-[red]">
                  Name is already taken!
                </div>
              )}
            </div>
          )}
        </div>
      </motion.div>
      <motion.div
        animate={{ translateX: 0 }}
        transition={{ duration: 0.2, ease: "easeOut" }}
        style={{ translateX: 200 }}
        className="h-full bg-[#f9fafb] flex flex-col overflow-hidden"
      >
        {scheduleConfig.frequency === DataExportFrequency.DAILY && (
          <DailyConfigurator
            timeFrames={scheduleConfig.timeFrames}
            onChange={changeTimeFrames}
            onAdd={addTimeFrame}
            onRemove={removeTimeFrame}
          />
        )}
        {scheduleConfig.frequency === DataExportFrequency.WEEKLY && (
          <WeeklyConfigurator
            timeFrames={scheduleConfig.timeFrames}
            onChange={changeTimeFrames}
            onAdd={addTimeFrame}
            onRemove={removeTimeFrame}
          />
        )}
        {scheduleConfig.frequency === DataExportFrequency.MONTHLY && (
          <MonthlyConfigurator
            timeFrame={scheduleConfig.timeFrames[0]}
            onChange={(value) => changeTimeFrames([value])}
          />
        )}
      </motion.div>
    </motion.div>
  );
}
